/**
 * PRP-DAMAGE-1 (work item 1): the damage marks the chat read from the notes, grouped by room,
 * inside the Claim facts section, plus the notes coverage line (D4).
 *
 * Each mark shows surface, material, work and extent, its confidence (inferred / low badged,
 * "awaiting confirmation" until the interview confirms it), what became of it (lines generated,
 * open item, rejected, no line + reason), and its source: the note id with the note's own text,
 * or the photo name. Area rooms (exterior, roof, lists) group like any other room. Labels only:
 * the server sends no codes, rule ids or prices. Text is data, shown as text.
 */
import type { ClaimDamageMark, ClaimNotesCoverage } from '../../lib/mcpApi';
import { Pill } from './ui/pill';
import type { PillKind } from './ui/pill';

function statusPill(m: ClaimDamageMark): { text: string; kind: PillKind } {
  switch (m.status) {
    case 'lines': {
      const n = m.line_count ?? 0;
      return { text: `${n} line${n === 1 ? '' : 's'} generated`, kind: 'ok' };
    }
    case 'open_item':
      return { text: 'open item', kind: 'warn' };
    case 'awaiting_confirmation':
      return { text: 'awaiting confirmation', kind: 'warn' };
    case 'rejected':
      return { text: 'rejected', kind: 'neutral' };
    case 'no_line':
      return { text: 'no line', kind: 'neutral' };
    default:
      return { text: String(m.status ?? 'unknown'), kind: 'neutral' };
  }
}

const LOW = new Set(['inferred', 'low', 'unknown']);

function MarkRow({ m }: { m: ClaimDamageMark }) {
  const st = statusPill(m);
  const what = [m.surface_label || m.surface, m.material, m.work_label || m.work, m.extent_label]
    .filter((v) => v)
    .join(' · ');
  const low = m.confidence ? LOW.has(m.confidence) : false;
  return (
    <li data-testid={`damage-mark-${m.mark_id}`} className="break-words">
      <span className="font-[550]">{what || 'damage'}</span>{' '}
      <span className="inline-flex flex-wrap items-center gap-1 align-middle">
        <Pill kind={st.kind} className="h-4 px-1.5">
          {st.text}
        </Pill>
        {low ? (
          <Pill kind="warn" className="h-4 px-1.5">
            {m.confidence}
          </Pill>
        ) : (
          m.confidence && <span className="text-[11px] text-[var(--tx-faint)]">{m.confidence}</span>
        )}
      </span>
      {m.reason && <span className="ml-1 text-[11px] text-[var(--tx-faint)]">({m.reason})</span>}
      <span className="block text-[11px] text-[var(--tx-faint)]">
        {m.source_kind === 'photo' ? (
          <>from photo {m.source || 'not given'}</>
        ) : (
          <>
            from note {m.source || 'not given'}
            {m.source_text && <>: "{m.source_text}"</>}
          </>
        )}
        {m.cause && <> · cause: {m.cause}</>}
      </span>
    </li>
  );
}

/** "N notes: x marked, y open, z no scope, w unresolved". */
export function NotesCoverageLine({ c }: { c: ClaimNotesCoverage }) {
  return (
    <p className="mt-2 text-[12px] text-[var(--tx-mid)]" data-testid="notes-coverage">
      {c.total} note{c.total === 1 ? '' : 's'}: {c.marked} marked, {c.open_item} open, {c.no_scope} no scope,{' '}
      <span className={c.unresolved > 0 ? 'font-[600] text-[var(--warn)]' : undefined}>
        {c.unresolved} unresolved
      </span>
    </p>
  );
}

/** The "Damage marks" block, grouped by room in the order the rooms first appear. */
export default function ClaimDamageMarks({ marks }: { marks: ClaimDamageMark[] }) {
  const byRoom = new Map<string, ClaimDamageMark[]>();
  for (const m of marks) {
    const room = m.room || 'No room';
    byRoom.set(room, [...(byRoom.get(room) ?? []), m]);
  }
  return (
    <div className="mt-3" data-testid="damage-marks">
      <h3 className="mb-1 text-[12px] font-[600] text-[var(--tx-lo)]">Damage marks</h3>
      {[...byRoom.entries()].map(([room, list]) => (
        <div key={room} className="mt-1.5" data-testid="damage-room">
          <h4 className="text-[12.5px] font-[600] text-[var(--tx-hi)]">{room}</h4>
          <ul className="space-y-1 pl-2 text-[13px] text-[var(--tx-hi)]">
            {list.map((m) => (
              <MarkRow key={m.mark_id} m={m} />
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
